import { SyntheticEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { openSlider } from 'utils/sportsbook/api-post-message-sender';
import { ResponseGeneric } from 'interfaces/generic';
import { UserNotifications } from 'interfaces/notifications';
import { AFTER_SIGN_IN_ACTION_TYPES, AfterSignIn } from 'services/afterSignIn';
import { setGamesNotifications } from 'services/sportsbook/notifications';
import { addQuery, saveCurrentLocation } from 'components/QueryRoute';
import { setUserNotifications } from 'store/actions/sport-data';

type Props = {
  notificationLoading: boolean;
  setNotificationLoading: (arg: boolean) => void;
  game: { id: number; start_ts: number };
  userSportNotifications: UserNotifications;
  shouldSubscribeForNotifications: boolean;
  userPending: boolean;
  isLoggedIn: boolean;
};

export const useSportsNotifications = ({
  notificationLoading,
  setNotificationLoading,
  game,
  userSportNotifications,
  shouldSubscribeForNotifications,
  userPending,
  isLoggedIn
}: Props): { handleNotificationClick: (e: SyntheticEvent) => void } => {
  const dispatch = useDispatch();
  const history = useHistory();

  const updateGamesNotifications = useCallback(() => {
    return new Promise(resolve => {
      setNotificationLoading(true);
      setGamesNotifications(
        { id: game.id, game_start_ts: game.start_ts },
        shouldSubscribeForNotifications,
        (resp: ResponseGeneric) => {
          if (resp.details.code === 0) {
            dispatch(
              setUserNotifications({
                [game.id]: {
                  events: [
                    {
                      event_name: 'MatchFinished',
                      is_subscribed: shouldSubscribeForNotifications
                    }
                  ]
                }
              })
            );
            setNotificationLoading(false);
          }

          resolve(true);
        },
        () => {
          setNotificationLoading(false);
          resolve(true);
        }
      );
    });
  }, [
    userSportNotifications,
    game.id,
    game.start_ts,
    shouldSubscribeForNotifications
  ]);

  const handleNotificationClick = useCallback(
    async (e: SyntheticEvent) => {
      e.stopPropagation();

      if (notificationLoading) {
        return;
      }

      if (!userPending) {
        if (isLoggedIn) {
          await updateGamesNotifications();
        } else {
          if (SpringConfigs.IFRAME_SPORTSBOOK) {
            openSlider('login');
          } else {
            saveCurrentLocation();
            history.push(addQuery({ accounts: '*', login: '*' }));
            const afterSignIn = new AfterSignIn();
            afterSignIn.appendAction(
              AFTER_SIGN_IN_ACTION_TYPES.SUBSCRIBE_FOR_NOTIFICATION,
              updateGamesNotifications
            );
          }
        }
      }
    },
    [
      userPending,
      isLoggedIn,
      shouldSubscribeForNotifications,
      notificationLoading
    ]
  );

  return {
    handleNotificationClick
  };
};
